import { all, create } from 'mathjs';

/**
 * Revenue model of the sales simulator. Same growth model and additionality rules as the platform's forests
 * (views/Areas/AreaItem.vue, utils/renderTreeTypeData.js):
 *
 *   growth        each tree type's theoretical formula = tonnes of CO2 per tree at a given age (mathjs: year, density_kg)
 *   base CO2      the forest's natural growth: the yearly increment of its stock, from today on
 *   fertilizing   extra growth = base increment x the tree type's fertilizing factor for the years since application
 *   thinning      both thin the forest down to the target, but conventional harvesters are bigger and cut 20% more
 *                 trees (by default): the growth of those trees Zerticarbon keeps is additional
 *   final cut     without Zerticarbon every tree is felled in the planned year (replanted, the young stand captures
 *                 almost nothing for years); kept standing, its stock and its growth beyond the replanted stand's are
 *                 additional
 *
 * Two scenarios are computed: with Zerticarbon (drone scan, light thinning, fertilizer, no final cut) and without it,
 * conventional management (heavier thinning, no fertilizer, the final cut). Each practice is worth what it adds over
 * the conventional way, so it is never negative.
 *
 * Everything here is pure: (inputs, model) -> results, so the page recomputes on every keystroke.
 */

const math = create(all);
const compiled = new Map();

export const ROTATION = { pine: 70, spruce: 65, birch: 55, oak: 90, other: 60 };
const GENUS_FALLBACK = { pine: 'Pine FIN VT1', spruce: 'Spruce FIN MT1', birch: 'Birch SouthFIN' };

export function genusOf(name) {
  const n = String(name || '').toLowerCase();
  if (/pine|pinus/.test(n)) return 'pine';
  if (/spruce|picea/.test(n)) return 'spruce';
  if (/birch|betula/.test(n)) return 'birch';
  if (/quercus|oak/.test(n)) return 'oak';
  return 'other';
}

// Typical trees per hectare, used only when neither the forest data nor the platform's forests give one (editable)
const TYPICAL_DENSITY = [
  [/suber/i, 150], [/ilex/i, 300], [/faginea/i, 400], [/pinea/i, 300], [/halepensis/i, 500], [/radiata/i, 1100],
  [/quercus|oak/i, 350], [/pinus/i, 500], [/acer/i, 500],
];
const GENUS_DENSITY = { pine: 800, spruce: 1000, birch: 900, oak: 350, other: 600 };
export function typicalDensity(name) {
  const hit = TYPICAL_DENSITY.find(([re]) => re.test(String(name || '')));
  return hit ? hit[1] : GENUS_DENSITY[genusOf(name)];
}

function compile(formula) {
  if (!compiled.has(formula)) {
    let fn;
    try {
      fn = math.parse(formula).compile();
    } catch (e) {
      fn = null;
    }
    compiled.set(formula, fn);
  }
  return compiled.get(formula);
}

/** Tonnes of CO2 in one tree of this type at `age` years (never negative). Memoised per type. */
export function perTreeFn(treeType) {
  const fn = compile(treeType.formula);
  const memo = new Map();
  return (age) => {
    const a = Math.max(0, Math.round(age));
    if (!memo.has(a)) {
      let v = 0;
      if (fn && a > 0) {
        try {
          v = Number(fn.evaluate({ year: a, density_kg: treeType.densityKg || 0 }));
        } catch (e) {
          v = 0;
        }
      }
      memo.set(a, Number.isFinite(v) && v > 0 ? v : 0);
    }
    return memo.get(a);
  };
}

/** Every year from the first to the last of a curve; a year missing in the data takes the line between its neighbours. */
function densify(curve) {
  const points = [...curve].sort((a, b) => a[0] - b[0]);
  if (points.length < 2) return points;
  const out = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const [y0, f0] = points[i];
    const [y1, f1] = points[i + 1];
    for (let y = y0; y < y1; y += 1) out.push([y, f0 + ((f1 - f0) * (y - y0)) / (y1 - y0)]);
  }
  out.push(points[points.length - 1]);
  return out;
}

/** Factor curve [[yearSinceApplication, factor]] of a fertilizer for a tree type (or the closest one we have). */
export function fertilizingCurve(treeType, fertilizer, treeTypes) {
  const own = treeType.fertilizing && treeType.fertilizing[fertilizer];
  if (own && own.length) return { curve: densify(own), estimated: false };
  const fallbackName = GENUS_FALLBACK[genusOf(treeType.name)];
  const fallback = fallbackName && treeTypes.find((t) => t.name === fallbackName);
  if (fallback && fallback.fertilizing && fallback.fertilizing[fertilizer] && fallback.fertilizing[fertilizer].length) {
    return { curve: densify(fallback.fertilizing[fertilizer]), estimated: true, from: fallback.name };
  }
  // average of every curve of that fertilizer
  const curves = treeTypes.map((t) => t.fertilizing && t.fertilizing[fertilizer]).filter((c) => c && c.length).map(densify);
  if (!curves.length) return { curve: [], estimated: true };
  const byYear = new Map();
  for (const c of curves) for (const [y, f] of c) byYear.set(y, [...(byYear.get(y) || []), f]);
  return { curve: [...byYear].sort((a, b) => a[0] - b[0]).map(([y, fs]) => [y, fs.reduce((a, b) => a + b, 0) / fs.length]), estimated: true, from: 'average' };
}

const clampShare = (s) => (Number.isFinite(Number(s)) && Number(s) > 0 ? Number(s) : 0);

/** The species of the inputs with their trees, growth function and normalised share. */
function speciesOf(inputs, model) {
  const rows = (inputs.species || []).map((s) => ({ ...s, type: model.treeTypes.find((t) => t.id === s.treeTypeId) })).filter((s) => s.type && clampShare(s.share));
  const total = rows.reduce((a, s) => a + clampShare(s.share), 0) || 1;
  const trees = Math.max(0, Number(inputs.areaHa) || 0) * Math.max(0, Number(inputs.density) || 0);
  return rows.map((s) => ({ type: s.type, share: clampShare(s.share) / total, trees: trees * (clampShare(s.share) / total), f: perTreeFn(s.type) }));
}

/** Dominant genus of the mix (for rotation ages and the scene). */
export function dominantGenus(inputs, model) {
  const sp = speciesOf({ ...inputs, areaHa: 1, density: 1 }, model).sort((a, b) => b.share - a.share);
  return sp.length ? genusOf(sp[0].type.name) : 'pine';
}

/** Default planned final cut (years from now) for a stand of that mix and age. */
export function defaultCutIn(inputs, model) {
  const rotation = ROTATION[dominantGenus(inputs, model)] || ROTATION.other;
  return Math.max(1, Math.round(rotation - (Number(inputs.age) || 0)));
}

export function simulate(inputs, model) {
  const period = Math.max(1, Math.min(100, Math.round(Number(inputs.period) || 60)));
  const startYear = Number(inputs.startYear) || new Date().getFullYear();
  const age = Math.max(0, Number(inputs.age) || 0);
  const density = Math.max(0, Number(inputs.density) || 0);
  const areaHa = Math.max(0, Number(inputs.areaHa) || 0);
  const prices = inputs.prices || {};
  const basePrice = Math.max(0, Number(prices.base) || 0);
  const addPrice = Math.max(0, Number(prices.additional) || 0);
  const growth = (Number(prices.growth) || 0) / 100;
  const fee = Math.min(100, Math.max(0, Number(prices.fee) || 0)) / 100;
  const practices = inputs.practices || {};
  const emissions = inputs.emissions || {};
  const warnings = [];
  const species = speciesOf(inputs, model);

  // ---- practices
  const thin = practices.thinning || {};
  const target = Math.max(1, Number(thin.targetDensity) || 1000);
  const thinOn = !!thin.enabled && density > target;
  if (thin.enabled && !thinOn) warnings.push(`Thinning has no effect: the forest already has ${Math.round(density)} trees/ha or fewer (target ${Math.round(target)}).`);
  const thinPct = thinOn ? 1 - target / density : 0;
  const thinAt = Math.max(1, Math.round(Number(thin.start) || 1));
  const boost = Math.max(0, Number(thin.boost === undefined ? 20 : thin.boost) || 0) / 100;

  const fert = practices.fertilizing || {};
  const fertOn = !!fert.enabled && !!fert.fertilizer;
  const fertStart = Math.max(1, Math.round(Number(fert.start) || 1));
  const fertRepeat = Math.max(0, Math.round(Number(fert.repeat) || 0));
  const applications = [];
  if (fertOn) for (let t = fertStart; t <= period; t += fertRepeat || period + 1) applications.push(t);
  const curves = new Map();
  if (fertOn) {
    for (const s of species) {
      const c = fertilizingCurve(s.type, fert.fertilizer, model.treeTypes);
      curves.set(s.type.id, new Map(c.curve));
      if (c.estimated) warnings.push(`${s.type.name}: no ${fert.fertilizer.toLowerCase()} data of its own, using ${c.from === 'average' ? 'the average curve' : `the ${c.from} curve`}.`);
    }
  }

  const cut = practices.finalCut || {};
  const cutAt = Math.max(1, Math.round(Number(cut.plannedIn) || 1));
  const cutOn = !!cut.enabled && cutAt <= period;
  if (cut.enabled && !cutOn) warnings.push('The planned final cut is after the end of the project: postponing it adds nothing within the period.');

  // ---- year by year
  const years = [];
  const stockToday = species.reduce((a, s) => a + s.trees * s.f(age), 0);
  const convThinPct = thinOn ? Math.min(0.95, thinPct * (1 + boost)) : 0; // a conventional harvester cuts `boost` more trees
  let projectStock = stockToday; // the stand Zerticarbon keeps (base path: its growth after the light thinning)
  let baselineStock = stockToday; // the final-cut reference: felled in the planned year, replanted
  let convStock = stockToday; // the forest under conventional management
  let lastGap = 0; // projectStock - baselineStock
  let cum = { base: 0, add: 0, gross: 0, net: 0, conv: 0, growth: 0 };
  const parts = { fertilizing: 0, thinning: 0, finalCut: 0, emissions: 0, emFertilizing: 0, emThinning: 0 };
  const partsEur = { fertilizing: 0, thinning: 0, finalCut: 0 }; // what each practice earns the owner (after the fee)

  for (let t = 1; t <= period; t += 1) {
    const thinned = thinOn && t >= thinAt;
    const remaining = thinned ? 1 - thinPct : 1;
    const convRemaining = thinned ? 1 - convThinPct : 1;
    let naturalInc = 0; // whole stand, unthinned
    let baseInc = 0; // what the stand Zerticarbon keeps grows
    let convInc = 0; // what the conventionally thinned stand grows
    let fertInc = 0;
    for (const s of species) {
      const inc = Math.max(0, s.trees * (s.f(age + t) - s.f(age + t - 1)));
      naturalInc += inc;
      const kept = inc * remaining;
      baseInc += kept;
      convInc += inc * convRemaining;
      if (fertOn) {
        const curve = curves.get(s.type.id);
        let factor = 0;
        for (const a of applications) if (t >= a && curve.has(t - a)) factor += curve.get(t - a);
        fertInc += kept * factor;
      }
    }
    // the trees Zerticarbon keeps and a conventional harvester would also have cut keep growing (platform rule)
    const thinInc = thinned ? naturalInc * thinPct * boost : 0;

    // stocks
    if (thinOn && t === thinAt) {
      projectStock *= 1 - thinPct;
      convStock *= 1 - convThinPct;
    }
    projectStock += baseInc;

    let baseCredit = baseInc;
    let cutInc = 0;
    if (cutOn && t >= cutAt) {
      // without Zerticarbon: every tree felled at cutAt, replanted, regrowing from age 0
      const regrowAge = t - cutAt;
      const replanted = species.reduce((a, s) => a + s.trees * s.f(regrowAge), 0);
      const replantedBefore = regrowAge > 0 ? species.reduce((a, s) => a + s.trees * s.f(regrowAge - 1), 0) : 0;
      const regrowth = t === cutAt ? 0 : Math.max(0, replanted - replantedBefore);
      baselineStock = replanted;
      baseCredit = regrowth;
      convInc = regrowth;
      convStock = replanted;
      const gap = projectStock - baselineStock;
      cutInc = gap - lastGap;
      lastGap = gap;
    } else {
      baselineStock = projectStock;
      convStock += convInc;
    }

    const emFert = fertOn && applications.includes(t) ? areaHa * Math.max(0, Number(emissions.fertilizing) || 0) : 0;
    const emThin = thinOn && t === thinAt ? areaHa * Math.max(0, Number(emissions.thinning) || 0) : 0;
    const emitted = emFert + emThin;

    const addCredit = fertInc + thinInc + cutInc - emitted;
    parts.fertilizing += fertInc;
    parts.thinning += thinInc;
    parts.finalCut += cutInc;
    parts.emissions += emitted;
    parts.emFertilizing += emFert;
    parts.emThinning += emThin;

    const pBase = basePrice * (1 + growth) ** (t - 1);
    const pAdd = addPrice * (1 + growth) ** (t - 1);
    const keep = 1 - fee;
    partsEur.fertilizing += (fertInc - emFert) * pAdd * keep;
    partsEur.thinning += (thinInc - emThin) * pAdd * keep;
    partsEur.finalCut += cutInc * pAdd * keep;
    const baseEur = baseCredit * pBase;
    const addEur = addCredit * pAdd;
    const gross = baseEur + addEur;
    const net = gross * keep;
    // CO2 the Zerticarbon forest takes from the air that year: its growth, with the fertilizer's
    const growthInc = baseInc + fertInc;
    cum = { base: cum.base + baseCredit, add: cum.add + addCredit, gross: cum.gross + gross, net: cum.net + net, conv: cum.conv + convInc, growth: cum.growth + growthInc };
    years.push({
      t, year: startYear + t, standAge: age + t,
      baseTons: baseCredit, addTons: addCredit, fertTons: fertInc, thinTons: thinInc, cutTons: cutInc, emitted,
      basePrice: pBase, addPrice: pAdd, baseEur, addEur, gross, fee: gross - net, net,
      cumBaseTons: cum.base, cumAddTons: cum.add, cumGross: cum.gross, cumNet: cum.net,
      convTons: convInc, cumConvTons: cum.conv, convStock, growthTons: growthInc, cumGrowthTons: cum.growth,
      projectStock: projectStock + parts.fertilizing, baselineStock,
      remainingShare: remaining,
    });
  }

  const trees = species.reduce((a, s) => a + s.trees, 0);
  const totals = {
    period, areaHa, density, age, startYear, endYear: startYear + period,
    trees,
    storedToday: stockToday,
    storedTodayValue: stockToday * basePrice,
    storedEnd: years.length ? years[years.length - 1].projectStock : stockToday,
    baseTons: cum.base,
    addTons: cum.add,
    tons: cum.base + cum.add,
    baseEur: years.reduce((a, y) => a + y.baseEur, 0),
    addEur: years.reduce((a, y) => a + y.addEur, 0),
    gross: cum.gross,
    fee: cum.gross - cum.net,
    clientNet: cum.net,
    perYear: cum.net / period,
    perHaYear: areaHa > 0 ? cum.net / period / areaHa : 0,
    firstYearNet: years.length ? years[0].net : 0,
    tonsPerHaYear: areaHa > 0 ? (cum.base + cum.add) / period / areaHa : 0,
    tonsPerYear: (cum.base + cum.add) / period,
    // the same forest without Zerticarbon (conventional management) and what the Zerticarbon forest itself captures
    convTons: cum.conv,
    convStockEnd: convStock,
    growthTons: cum.growth,
    felledShare: thinOn ? thinPct : 0,
    convFelledShare: cutOn ? 1 : convThinPct,
    convThinPct,
    thinPct,
    thinAt: thinOn ? thinAt : null,
    cutAt: cutOn ? cutAt : null,
    fertilizingAt: applications,
    parts,
    partsEur,
    species: species.map((s) => ({ id: s.type.id, name: s.type.name, share: s.share, trees: s.trees, storedToday: s.trees * s.f(age), storedEnd: s.trees * s.f(age + period) })),
  };
  return { years, totals, warnings };
}

/** The same inputs with one practice switched on or off (to show what each practice would add). */
export function withPractice(inputs, key, enabled) {
  return { ...inputs, practices: { ...(inputs.practices || {}), [key]: { ...((inputs.practices || {})[key] || {}), enabled } } };
}

/** Headline numbers stored with a saved simulation (for the list). */
export function summaryOf(result, inputs) {
  const t = result.totals;
  const r = (v, d = 2) => Math.round(v * 10 ** d) / 10 ** d;
  return {
    clientNet: r(t.clientNet), gross: r(t.gross), baseEur: r(t.baseEur), addEur: r(t.addEur),
    baseTons: r(t.baseTons), additionalTons: r(t.addTons), tons: r(t.tons), conventionalTons: r(t.convTons), storedToday: r(t.storedToday),
    perYear: r(t.perYear), perHaYear: r(t.perHaYear), period: t.period, areaHa: r(t.areaHa),
    basePrice: Number(inputs.prices && inputs.prices.base) || 0,
    additionalPrice: Number(inputs.prices && inputs.prices.additional) || 0,
    practices: Object.entries(inputs.practices || {}).filter(([, p]) => p && p.enabled).map(([k]) => k),
  };
}

// What the certified tonnes of CO2 are equivalent to, with the source of every factor (shown on the page)
export const EQUIVALENCES = [
  {
    key: 'cars', icon: 'fas fa-car-side', per: 4.29, label: 'cars off the road for a year',
    source: '4.29 t CO₂e per average passenger car driven for one year (US, 2022 data).',
    by: 'US EPA · Greenhouse Gas Equivalencies Calculator',
    url: 'https://www.epa.gov/energy/greenhouse-gases-equivalencies-calculator-calculations-and-references',
  },
  {
    key: 'flights', icon: 'fas fa-plane-departure', per: 0.636, label: 'Madrid–New York return flights',
    source: '0.64 t CO₂ per passenger for Madrid–New York and back in economy (2 × 318 kg), estimated from ICAO data. Only CO₂: the extra warming of contrails is not included.',
    by: 'ICAO data · Universidad de Alcalá carbon footprint calculator',
    url: 'https://huellaco2.web.uah.es/en/transaereo.php',
  },
  {
    key: 'homes', icon: 'fas fa-house', per: 7.45, label: "homes' energy for a year",
    source: "7.45 t CO₂ per home's energy use for one year: electricity, gas, propane and heating oil (US average).",
    by: 'US EPA · Greenhouse Gas Equivalencies Calculator',
    url: 'https://www.epa.gov/energy/greenhouse-gases-equivalencies-calculator-calculations-and-references',
  },
];
